import { z } from 'zod';
export const RoadmapEntityType = z.enum(['initiative', 'epic', 'feature', 'release', 'milestone', 'note']);
export const RoadmapHorizon = z.enum(['now', 'next', 'later', 'future']);
export const RoadmapHealth = z.enum(['on_track', 'at_risk', 'blocked', 'done', 'unknown']);
export const RoadmapItem = z.object({
    id: z.string(),
    entityType: z.string(),
    entityId: z.string().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    ownerId: z.string().nullable(),
    startDate: z.string().nullable(),
    targetDate: z.string().nullable(),
    roadmapStatus: z.string().nullable(),
    healthStatus: z.string().nullable(),
    horizon: z.string().nullable(),
    progress: z.number(),
    parentRoadmapItemId: z.string().nullable(),
    color: z.string().nullable(),
    sortOrder: z.number(),
    // computed
    customerName: z.string().nullable().optional(),
    code: z.string().nullable().optional(),
});
export const CreateRoadmapItemInput = z.object({
    entityType: RoadmapEntityType.default('note'),
    entityId: z.string().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    ownerId: z.string().optional(),
    startDate: z.string().nullable().optional(),
    targetDate: z.string().nullable().optional(),
    roadmapStatus: z.string().optional(),
    healthStatus: RoadmapHealth.optional(),
    horizon: RoadmapHorizon.optional(),
    progress: z.number().optional(),
    parentRoadmapItemId: z.string().nullable().optional(),
    color: z.string().optional(),
});
// CreateRoadmapItemInput has no sortOrder, so .partial() alone never permits it —
// add it explicitly so drag-and-drop reordering can persist (#18).
export const UpdateRoadmapItemInput = CreateRoadmapItemInput.partial().extend({
    sortOrder: z.number().int().optional(),
});
/** Persist a drag-and-drop reorder: the new order of the affected group's ids (#18). */
export const ReorderRoadmapInput = z.object({
    orderedIds: z.array(z.string()).min(1),
});
// --- AI-proposed roadmap ranking (#18) -------------------------------------
export const RoadmapValueMetric = z.enum(['arr', 'ltv', 'tcv', 'blended']);
export const RoadmapQuadrant = z.enum(['quick_wins', 'big_bets', 'fill_ins', 'money_pits']);
/** Per-item scoring breakdown so the UI can show every term that fed the score. */
export const RoadmapScoreBreakdown = z.object({
    value: z.number(),
    valueMetric: RoadmapValueMetric,
    effortDays: z.number(),
    rdCost: z.number(),
    quadrant: RoadmapQuadrant,
    roi: z.number(),
    upvotes: z.number(),
    score: z.number(),
    missingValue: z.boolean(),
});
export const RankedRoadmapItem = z.object({
    id: z.string(),
    title: z.string(),
    code: z.string().nullable(),
    entityId: z.string().nullable(),
    entityType: z.string(),
    currentSortOrder: z.number(),
    proposedRank: z.number(),
    rankDelta: z.number(),
    breakdown: RoadmapScoreBreakdown,
    rationale: z.string().nullable(),
});
export const RoadmapRankInput = z.object({
    valueMetric: RoadmapValueMetric.default('blended'),
    weights: z.object({ value: z.number(), effort: z.number(), upvotes: z.number() }).partial().optional(),
    persist: z.boolean().default(false), // write the proposed order to sortOrder
    narrative: z.boolean().default(false), // ask the AI for a per-item rationale
});
export const RoadmapRankResult = z.object({
    items: z.array(RankedRoadmapItem),
    unscored: z.array(z.object({ id: z.string(), title: z.string(), code: z.string().nullable() })),
    blendedRdRatePerHour: z.number(),
    source: z.enum(['llm', 'heuristic', 'deterministic']),
    note: z.string().optional(),
});
export const RoadmapDependencyType = z.enum(['blocks', 'is_blocked_by', 'relates_to', 'requires', 'depends_on', 'duplicates', 'caused_by']);
export const RoadmapDependency = z.object({
    id: z.string(),
    sourceItemId: z.string(),
    targetItemId: z.string(),
    dependencyType: z.string(),
    status: z.string().nullable(),
    riskLevel: z.string().nullable(),
});
export const CreateRoadmapDependencyInput = z.object({
    sourceItemId: z.string(),
    targetItemId: z.string(),
    dependencyType: RoadmapDependencyType.default('blocks'),
    riskLevel: z.string().optional(),
});
export const RoadmapMilestone = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    milestoneDate: z.string().nullable(),
    milestoneType: z.string().nullable(),
    ownerId: z.string().nullable(),
    status: z.string().nullable(),
    linkedReleaseId: z.string().nullable(),
});
export const CreateMilestoneInput = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    milestoneDate: z.string().nullable().optional(),
    milestoneType: z.string().optional(),
    status: z.string().optional(),
    linkedReleaseId: z.string().optional(),
});
export const UpdateMilestoneInput = CreateMilestoneInput.partial();
//# sourceMappingURL=roadmap.js.map